import SharedWorker from '@okikio/sharedworker'
import { atom, getDefaultStore } from 'jotai'
import { uniqueId } from 'lodash-es'
import type { RefObject, PropsWithChildren } from 'react'
import {
  createContext,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import type { DynamicConstants } from 'src/services/constants'
import { useDynamicConstants } from 'src/services/constants'
import type {
  CompletionRequest,
  CompletionResponse,
  CompletionResultResponse,
  TagCompleteRequest,
} from 'src/workers/tagCompleteType'

try {
  if (window.caches) caches.delete('tag-complete')
} catch {
  // ignore
}

async function getPromptsCompleteWorkerUrl() {
  // dummy to trick vite
  // see https://vite.dev/guide/features.html#import-with-constructors
  class Worker {
    constructor(
      public url: URL,
      public options?: WorkerOptions,
    ) {}
  }
  // Use absolute path (/src/...) instead of relative path (src/...) because
  // tsconfig.json excludes "src/workers" directory, which can cause path
  // resolution issues during SSR builds. The leading slash ensures Vite
  // resolves the path from the project root regardless of TypeScript config.
  const { url } = new Worker(
    new URL('/src/workers/tagComplete.worker.ts', import.meta.url),
    { type: 'module' },
  )

  if (import.meta.env.DEV) return url.toString()

  const code = await (await fetch(url)).text()
  return `data:application/javascript,${encodeURIComponent(code)}`
}

const defaultStore = getDefaultStore()

interface PendingCompletionRequest {
  resolve: (result: CompletionResultResponse) => void
  reject: (err: Error) => void
  request: CompletionRequest
}

export class CompletionCancelledError extends Error {
  constructor() {
    super('Completion request cancelled')
    this.name = 'CompletionCancelledError'
  }
}

const DEBOUNCE_MS = 150

export class PromptsCompletionWorkerController {
  private worker: SharedWorker | null

  loadingProgressAtom = atom(0)

  loadingCompleteAtom = atom(get => get(this.loadingProgressAtom) >= 1)

  constructor(option: {
    workerUrl: string
    tagCompleteAssets: DynamicConstants['tagCompleteAssets']
  }) {
    this.worker = new SharedWorker(option.workerUrl, { type: 'module' })

    this.worker.postMessage({
      type: 'init',
      payload: {
        assets: option.tagCompleteAssets,
      },
    } satisfies TagCompleteRequest)

    this.worker.onerror = this.onerror
    this.worker.onmessage = this.onmessage
  }

  private onmessage = (event: MessageEvent) => {
    const data = event.data as CompletionResponse

    switch (data.type) {
      case 'progress': {
        defaultStore.set(this.loadingProgressAtom, data.progress)
        break
      }
      case 'completion': {
        if (data.requestId) {
          const request = this.pendingCompletionRequest.get(data.requestId)
          if (request) {
            request.resolve(data)
            this.pendingCompletionRequest.delete(data.requestId)
          }
        }
        break
      }
    }
  }

  private onerror = (event: ErrorEvent) => {
    toast.error(event.message)
    this.destroy()
  }

  private pendingCompletionRequest = new Map<string, PendingCompletionRequest>()
  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  private cancelPendingRequests() {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    for (const request of this.pendingCompletionRequest.values()) {
      request.reject(new CompletionCancelledError())
    }
    this.pendingCompletionRequest.clear()
  }

  requestCompletion(rawRequest: Omit<CompletionRequest, 'requestId'>) {
    if (this.worker === null) {
      throw new Error('PromptsCompletionWorkerController destroyed')
    }

    // Cancel any in-flight requests — only the latest keystroke matters
    this.cancelPendingRequests()

    return new Promise<CompletionResultResponse>((resolve, reject) => {
      const requestId = uniqueId('completion')
      const request = { ...rawRequest, requestId }
      this.pendingCompletionRequest.set(requestId, {
        resolve,
        reject,
        request,
      })

      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null
        // Always post — the worker returns empty results if not yet loaded,
        // which is better than leaving the promise hanging indefinitely.
        this.worker?.postMessage({
          type: 'completion',
          payload: request,
        } satisfies TagCompleteRequest)
      }, DEBOUNCE_MS)
    })
  }

  destroy() {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    if (this.worker) {
      this.worker.close()
      this.worker = null
    }
    for (const request of this.pendingCompletionRequest.values()) {
      request.reject(new Error('PromptsCompletionWorkerController destroyed'))
    }
    this.pendingCompletionRequest.clear()
  }
}

function PromptsCompletionWorkerControllerLoader(props: {
  updateWorkerController: (
    workerController: PromptsCompletionWorkerController | null,
  ) => void
}) {
  const { updateWorkerController: setWorkerController } = props
  const { data: workerUrl } = useSWR(
    'worker:tagComplete',
    getPromptsCompleteWorkerUrl,
    {
      suspense: true,
    },
  )
  const { data: { tagCompleteAssets } = {} } = useDynamicConstants({
    suspense: true,
  })

  // TODO: add back character & world support when we enable that feature again

  useEffect(() => {
    const workerController = new PromptsCompletionWorkerController({
      workerUrl,
      tagCompleteAssets,
    })
    setWorkerController(workerController)
    return () => {
      workerController.destroy()
      setWorkerController(null)
    }
  }, [setWorkerController, tagCompleteAssets, workerUrl])
  return null
}

interface PromptsCompletionContextValue {
  workerController: PromptsCompletionWorkerController | null
  workerControllerRef: RefObject<PromptsCompletionWorkerController | null>
}

export const PromptsCompletionContext =
  createContext<PromptsCompletionContextValue>({
    workerController: null,
    workerControllerRef: { current: null },
  })

const canLoadWorker = typeof Worker !== 'undefined'

function PromptsCompletionProviderInner(
  props: PropsWithChildren<{
    enable?: boolean
  }>,
) {
  const canLoadWorker = typeof window !== 'undefined'
  const workerControllerRef = useRef<PromptsCompletionWorkerController | null>(
    null,
  )
  const [workerController, setWorkerController] =
    useState<PromptsCompletionWorkerController | null>(null)
  const updateWorkerController = useCallback(
    (workerController: PromptsCompletionWorkerController | null) => {
      setWorkerController(workerController)
      workerControllerRef.current = workerController
    },
    [],
  )
  const value = useMemo(
    () => ({ workerController, workerControllerRef }),
    [workerController, workerControllerRef],
  )
  return (
    <PromptsCompletionContext.Provider value={value}>
      <Suspense>
        {props.enable && canLoadWorker && (
          <PromptsCompletionWorkerControllerLoader
            updateWorkerController={updateWorkerController}
          />
        )}
      </Suspense>
      {props.children}
    </PromptsCompletionContext.Provider>
  )
}

export function PromptsCompletionProvider(
  props: PropsWithChildren<{
    enable?: boolean
  }>,
) {
  return (
    <ErrorBoundary fallback={<>{props.children}</>}>
      <PromptsCompletionProviderInner {...props} />
    </ErrorBoundary>
  )
}
